import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import Text from '@/utils/responsiveText';
import CustomInput from './CustomInput';
import {
  useChangePasswordMutation,
  useLogoutMutation,
  useOwnerChangePasswordMutation,
} from '@/store/api/authApi';
import { passwordSchema } from '@/utils/validation';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import { useDispatch } from 'react-redux';
import apiSlice from '@/store/api/apiSlice';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/store/api/apiErrorHandler';
import { Button } from '../ui/button';
import { useRoleCheck } from '@/hooks/useRoleCheck';

type FormValues = z.infer<typeof passwordSchema>;

const fields = [
  { name: 'oldPassword', label: 'Current Password', placeholder: 'Enter current password' },
  { name: 'newPassword', label: 'New Password', placeholder: 'Enter new password' },
  { name: 'confirmPassword', label: 'Confirm Password', placeholder: 'Confirm new password' },
];
interface ChangePasswordProps {
  close?: () => void;
  title?: boolean;
  inputClass?: string;
  lableClass?: string;
  role?: string;
}
export const ChangePassword = ({
  close,
  title = false,
  inputClass = '',
  lableClass = '',
}: ChangePasswordProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [OwnerChangePassword] = useOwnerChangePasswordMutation();
  const [logout] = useLogoutMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOwner } = useRoleCheck();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      let res;

      if (isOwner) {
        res = await OwnerChangePassword({
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        }).unwrap();
      } else {
        res = await changePassword({
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        }).unwrap();
      }

      if (res.status) {
        toast.success('Password changed successfully');
        await logout().unwrap();
        dispatch(clearAuth());
        dispatch(clearUser());
        apiSlice.util.resetApiState();
        navigate('/login', { replace: true });
        close?.();
      }
    } catch (err: any) {
      handleApiError(err, 'Failed to change password');
    }
  };

  return (
    <div className="text-white ">
      {' '}
      {title && (
        <Text
          min={18}
          max={22}
          as="h4"
          className="font-medium text-textcolor mb-2 pb-3 border-b border-neutral-500"
        >
          Create Password{' '}
        </Text>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-md mx-auto space-y-2">
        {fields.map((field) => (
          <CustomInput
            key={field.name}
            name={field.name as keyof FormValues}
            label={field.label}
            placeholder={field.placeholder}
            type="password"
            control={control}
            error={errors[field.name as keyof FormValues]}
            className="w-full"
            inputClassName={`${inputClass}`}
            labelClassName={`${lableClass} text-textcolor`}
          />
        ))}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full text-white py-2 !mt-4 px-4 rounded-md disabled:opacity-50"
        >
          {isLoading ? 'Changing...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};
